import React, { useState, useRef, useEffect, useCallback, memo } from 'react'
import { createPortal } from 'react-dom'

type BaseOption = {
  onClick?: (option: any) => void
}

export type ActionOption = {
  id: number
  label: string
  onClick?: (option: ActionOption) => void
}

type GenericDropdownIconProps<T extends BaseOption> = {
  options: T[]
  value?: T | null
  onChange?: (option: T) => void
  placeholder?: string
  disabled?: boolean
  getOptionLabel: (option: T) => string
  getOptionValue: (option: T) => string | number
  className?: string
}

const MENU_WIDTH = 170
const MENU_MAX_HEIGHT = 200

function GenericDropdownIconComponent<T extends BaseOption>({
  options,
  value = null,
  onChange,
  placeholder = 'Select...',
  disabled = false,
  getOptionLabel,
  getOptionValue,
  className = ''
}: GenericDropdownIconProps<T>) {

  const [isOpen, setIsOpen] = useState(false)
  const [menuStyle, setMenuStyle] = useState<React.CSSProperties>({})
  const dropdownRef = useRef<HTMLDivElement>(null)
  const menuRef = useRef<HTMLDivElement>(null)
  
  const calculatePosition = useCallback(() => {
    if (!dropdownRef.current) return {}

    const rect = dropdownRef.current.getBoundingClientRect()
    const spaceBelow = window.innerHeight - rect.bottom
    const openUpwards = spaceBelow < MENU_MAX_HEIGHT && rect.top > spaceBelow

    let left = rect.right - MENU_WIDTH
    left = Math.max(8, Math.min(left, window.innerWidth - MENU_WIDTH - 8))

    return {
      position: 'fixed' as const,
      left,
      top: openUpwards ? undefined : rect.bottom + 4,
      bottom: openUpwards ? window.innerHeight - rect.top + 4 : undefined,
      width: MENU_WIDTH,
      minWidth: MENU_WIDTH,
      maxHeight: MENU_MAX_HEIGHT,
      zIndex: 1060,
    }
  }, [])

  const handleToggle = (e: React.MouseEvent) => {
    e.stopPropagation()
    if (disabled) return
    if (!isOpen) {
      setMenuStyle(calculatePosition())
    }
    setIsOpen(prev => !prev)
  }

  useEffect(() => {
    if (!isOpen) return

    const handleScrollOrResize = () => {
      setMenuStyle(calculatePosition())
    }

    function handleClickOutside(event: MouseEvent) {
      const target = event.target as Node
      const clickedTrigger = dropdownRef.current?.contains(target)
      const clickedMenu = menuRef.current?.contains(target)

      if (!clickedTrigger && !clickedMenu) {
        setIsOpen(false)
      }
    }

    window.addEventListener('scroll', handleScrollOrResize, true)
    window.addEventListener('resize', handleScrollOrResize)
    document.addEventListener('mousedown', handleClickOutside)

    return () => {
      window.removeEventListener('scroll', handleScrollOrResize, true)
      window.removeEventListener('resize', handleScrollOrResize)
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [isOpen, calculatePosition])

  const handleSelect = (option: T) => {
    if (onChange) {
      onChange(option)
    }

    if (option.onClick) {
      option.onClick(option)
    }

    setIsOpen(false)
  }

  return (
    <div className='nb-language-autocomplete cursor-pointer' style={{ display: 'inline-block' }}>
        <div ref={dropdownRef} className={`${className}`}>
            <div
                className={`${disabled ? 'disabled' : ''}`}
                onClick={handleToggle}
                style={{ padding: '6px 8px', display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}
            >
                <svg width="12" height="12" viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg" className='icon icon-sm'>
                    <circle cx="1.5" cy="5.5" r="1.5" fill="#151515"/>
                    <circle cx="5.5" cy="5.5" r="1.5" fill="#151515"/>
                    <circle cx="9.5" cy="5.5" r="1.5" fill="#151515"/>
                </svg>
            </div>

            {isOpen && createPortal(
                <div ref={menuRef} className="nb-language-menu" style={menuStyle}>
                {options.map((option) => (
                    <div
                    key={getOptionValue(option)}
                    className="nb-language-item"
                    onClick={(e) => {
                      e.stopPropagation()
                      handleSelect(option)
                    }}
                    >
                    {getOptionLabel(option)}
                    </div>
                ))}
                </div>,
                document.body
            )}
        </div>
    </div>
  )
}

export const GenericDropdownIcon = memo(GenericDropdownIconComponent) as typeof GenericDropdownIconComponent

